<template>
    <section
        class="overflow-hidden rounded-lg border border-divider bg-surface"
        data-spec-class="base-accordion"
    >
        <div class="border-b border-divider px-3 py-3">
            <div class="flex flex-wrap items-center justify-between gap-3">
                <button
                    type="button"
                    class="flex flex-1 items-center gap-3 text-left"
                    :aria-expanded="isExpanded"
                    :disabled="isDisabled"
                    data-spec-class="base-accordion__toggle"
                    @click="toggle"
                >
                    <ChevronDownIcon
                        class="size-5 text-tertiary transition-transform"
                        :class="{ 'rotate-180': isExpanded }"
                    />
                    <div>
                        <BaseCardListTitle :theme="Theme.Secondary">
                            {{ title }}
                        </BaseCardListTitle>
                        <p
                            v-if="hint"
                            class="text-sm text-tertiary"
                        >
                            {{ hint }}
                        </p>
                    </div>
                </button>
                <slot
                    name="header-actions"
                    :is-expanded="isExpanded"
                />
            </div>
        </div>

        <BaseTransitionCollapse :is-expanded="isExpanded">
            <div
                class="mb-2 px-1 py-3"
                data-spec-class="base-accordion__body"
            >
                <slot />
            </div>
        </BaseTransitionCollapse>
    </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import BaseTransitionCollapse from './BaseTransitionCollapse.vue';
import BaseCardListTitle from '@/components/BaseCardListTitle.vue';
import { Theme } from '@/types';

interface Props {
    modelValue: boolean;
    title: string;
    hint?: string;
    isDisabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    hint: '',
    isDisabled: false,
});

const emits = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
}>();

const isExpanded = computed(() => props.modelValue);

const toggle = () => {
    if (props.isDisabled) {
        return;
    }

    emits('update:modelValue', !isExpanded.value);
};
</script>
